// ./src/mediaAttribution.ts
//
// ROADMAP A2e #95 — THE CREDIT A STOCK PICTURE OWES. Unsplash, GIPHY and Pexels require it wherever their media is
// shown: "Photo by <name> on Unsplash", the name linking to the photographer's profile and the service to its site
// (Unsplash: both links carry its referral UTM). The host app's media picker bakes the credit into the stored URL
// (`?stock=&author=&authorUrl=`, apps/app `stockMedia.ts` → `appendAttribution`), because a stock pick stores only a URL.
//
// ⚠ Shared, pure and DOM-free: the host app (`MediaCredit`) and the public event website (its credits line) must read
//   and render it the same way — it used to live in apps/app alone, so the site, where the pictures are actually shown
//   to the public, credited nobody.

export interface MediaAttribution {
  stock: string;
  author: string | null;
  authorUrl: string | null;
}

/**
 * Unsplash's referral tag — its API guidelines ask for `?utm_source=<app>&utm_medium=referral` on every link back.
 * A2e #101 (i, owner 2026-10-05) — the source is the SPACE the event website (or the dashboard) belongs to, passed by
 * the caller (`siteBrand.spaceId` on the site, the white label's `spaceId` in the dashboard) — never a name written
 * here. No source known → the link carries no tag rather than a guessed one.
 */
export const withUnsplashUtm = (url: string, referralSource?: string | null): string => {
  const source = String(referralSource ?? '').trim();

  if (!source) {
    return url;
  }

  try {
    const u = new URL(url);

    u.searchParams.set('utm_source', source);
    u.searchParams.set('utm_medium', 'referral');

    return u.toString();
  } catch {
    return url;
  }
};

/** Each service's display name and a link to it (attribution). */
export const STOCK_SERVICES: Record<string, { name: string; url: string }> = {
  unsplash: { name: 'Unsplash', url: 'https://unsplash.com/' },
  giphy: { name: 'GIPHY', url: 'https://giphy.com' },
  pexels: { name: 'Pexels', url: 'https://www.pexels.com' },
};

/** A service's name and link — Unsplash's tagged with `referralSource` (see `withUnsplashUtm`). */
export const stockServiceInfo = (stock: string, referralSource?: string | null): { name: string; url: string } => {
  const service = STOCK_SERVICES[stock] ?? { name: stock, url: '' };

  return stock === 'unsplash' && service.url ? { ...service, url: withUnsplashUtm(service.url, referralSource) } : service;
};

/**
 * The attribution baked into a stored stock URL (?stock=&author=&authorUrl=), or null for any other URL.
 * ⚠ Only a real http(s) URL counts — the site scans whole pages for these, and a stray "?stock=" in text is not one.
 */
export function parseMediaAttribution(url?: string | null): MediaAttribution | null {
  if (!url || !/^https?:\/\//i.test(url)) {
    return null;
  }

  const q = url.indexOf('?');

  if (q < 0) {
    return null;
  }

  try {
    const params = new URLSearchParams(url.slice(q + 1));
    const stock = params.get('stock');

    if (!stock || !STOCK_SERVICES[stock]) {
      return null;
    }

    const authorUrl = params.get('authorUrl');

    return {
      stock,
      author: params.get('author')?.trim() || null,
      // ⚠ A link we render: http(s) only (a `javascript:` URL baked into a stored picture must never become an href).
      authorUrl: authorUrl && /^https?:\/\//i.test(authorUrl) ? authorUrl : null,
    };
  } catch {
    return null;
  }
}

/** What a credit line says and links to — "{noun} by {author} on {service}". */
export interface MediaCreditParts {
  /** "Photo", "Video" or "GIF". */
  noun: string;
  author: string | null;
  /** The author's profile (Unsplash: with the referral UTM), or null. */
  authorHref: string | null;
  service: string;
  serviceHref: string;
}

const VIDEO_PATH = /\.(mp4|webm|mov|m4v)(\?|$)/i;

/**
 * The credit for one stored stock URL, or null when it carries none. `kindLabel` overrides the noun (e.g. "Video");
 * `referralSource` is the space the page belongs to (Unsplash's links are tagged with it).
 */
export function mediaCreditParts(url: string | null | undefined, kindLabel?: string, referralSource?: string | null): MediaCreditParts | null {
  const att = parseMediaAttribution(url);

  if (!att) {
    return null;
  }

  const service = stockServiceInfo(att.stock, referralSource);
  const noun = att.stock === 'giphy' ? 'GIF' : kindLabel || (VIDEO_PATH.test(String(url)) ? 'Video' : 'Photo');
  const authorHref = att.authorUrl ? (att.stock === 'unsplash' ? withUnsplashUtm(att.authorUrl, referralSource) : att.authorUrl) : null;

  return { noun, author: att.author, authorHref, service: service.name, serviceHref: service.url };
}

// Any http(s) URL carrying a `stock=` parameter, wherever it sits in a value: on its own, inside rich-text HTML
// (`src="…"`), or in CSS (`url(…)`). Stops at whitespace, quotes and brackets.
const STOCK_URL = /https?:\/\/[^\s"'<>()\\]+?[?&]stock=[a-z]+[^\s"'<>()\\]*/gi;

/**
 * ROADMAP A2e #95 — EVERY stock credit inside these values (an event, a page and its blocks' data …), each once, in the
 * order first met. A deep scan rather than a list of fields: a picture can come from a cover, a block's config, a
 * gallery item, a theme background or a rich-text body, and a list of fields is out of date the day a block gains an
 * image. `skipKeys` leaves out object keys whose pictures are not on this page (e.g. the other pages).
 * ⚠ Same person on the same service = one credit, whatever picture size the URL names.
 */
export function collectMediaCredits(values: unknown[], skipKeys: readonly string[] = [], referralSource?: string | null): MediaCreditParts[] {
  const out: MediaCreditParts[] = [];
  const seen = new Set<string>();
  const visited = new Set<object>();
  const skip = new Set(skipKeys);

  const take = (raw: string) => {
    if (raw.indexOf('stock=') < 0) {
      return;
    }

    // ⚠ Inside rich-text HTML the separators are `&amp;` — decoded first, or `&amp;stock=` is never seen as a parameter.
    const text = raw.replace(/&amp;/g, '&');

    for (const url of text.match(STOCK_URL) ?? []) {
      const parts = mediaCreditParts(url, undefined, referralSource);
      const key = parts ? `${parts.noun}|${parts.service}|${parts.author ?? ''}|${parts.authorHref ?? ''}` : '';

      if (parts && !seen.has(key)) {
        seen.add(key);
        out.push(parts);
      }
    }
  };

  const walk = (v: unknown, depth: number) => {
    if (depth > 40 || v == null) {
      return;
    }

    if (typeof v === 'string') {
      take(v);

      return;
    }

    if (typeof v !== 'object' || visited.has(v as object)) {
      return;
    }

    visited.add(v as object);

    if (Array.isArray(v)) {
      for (const item of v) {
        walk(item, depth + 1);
      }

      return;
    }

    for (const [key, item] of Object.entries(v as Record<string, unknown>)) {
      if (!skip.has(key)) {
        walk(item, depth + 1);
      }
    }
  };

  for (const value of values) {
    walk(value, 0);
  }

  return out;
}
